import { Button, Modal, Paper, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { OwnerType, TOwnerLink, TOwnershipStructure } from '../../types';
import { useFormContext } from 'react-hook-form';
import { styles } from '../../styles';
import { IndividualOwnerForm } from './IndividualOwnerForm';
import { LegalResidentOwnerForm } from './LegalResidentOwnerForm';
import { FreeFloatOwnerForm } from './FreeFloatOwnerForm';
import { SharedGroupOwnerForm } from './SharedGroup/SharedGroupOwnerForm';
import { ROOT_OWNER_NODE } from '../../constants';
import { setAllFieldsTouched } from '../../utils/utils';

interface IOwnerFormModalProps {
  index: number;
  onClose: () => void;
  handleCalculateOwnershipPercentage: () => void;
}

export const OwnerFormModal = ({
  index,
  onClose,
  handleCalculateOwnershipPercentage,
}: IOwnerFormModalProps) => {
  const { setValue, getValues, trigger } = useFormContext<TOwnershipStructure>();

  const handleLinkPercentChange = useCallback(() => {
    const link = getValues(`link.${index}`);
    const owners = getValues(`owners`);
    const parentIndex = owners.findIndex((item) => item.id === link.toNodeId);

    handleCalculateOwnershipPercentage();
    trigger(`owners.${parentIndex}`);
  }, [getValues, index]);

  const handlePercentageChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    const links = getValues(`link`);

    const currentLink = links[index];

    const cloneItems = links.filter(
      (item) =>
        item.fromNodeId === currentLink?.fromNodeId && item.toNodeId === currentLink.toNodeId
    );

    links.forEach((link, index) => {
      if (cloneItems.some((item) => item.id === link.id)) {
        setValue(`link.${index}.ownershipPercentage`, value);
      }
    });
  };

  const isFirstLevel = useMemo(() => {
    const ownerId = getValues(`link.${index}.fromNodeId`);
    const link = getValues('link');
    return link
      .filter((item: TOwnerLink) => item.fromNodeId === ownerId)
      .some((item: TOwnerLink) => item.toNodeId === ROOT_OWNER_NODE);
  }, [index, getValues]);

  if (index < 0) return null;

  const handleClose = async () => {
    const link = getValues(`link.${index}`);
    const owners = getValues('owners');
    const ownerIndex = owners.findIndex((owner) => owner.id === link.fromNodeId);

    if (isNaN(Number(link.ownershipPercentage))) {
      setValue(`link.${index}.ownershipPercentage`, 0);
    }

    await trigger([`owners.${ownerIndex}`, `link.${index}`]);

    setAllFieldsTouched(`owners.${ownerIndex}`, getValues(`owners.${ownerIndex}`), setValue);
    setAllFieldsTouched(`link.${index}`, getValues(`link.${index}`), setValue);
    handleLinkPercentChange();
    onClose();
  };

  const getOwnerForm = (index: number) => {
    const link = getValues(`link.${index}`);
    const owners = getValues('owners');
    const owner = owners.find((owner) => owner.id === link.fromNodeId);

    switch (owner?.type) {
      case OwnerType.INDIVIDUAL: {
        const links = getValues(`link`);
        const parentLinks = links.filter((item) => link.fromNodeId === item.fromNodeId);
        const parentOwners = owners.filter((owner) =>
          parentLinks.find((link) => link.toNodeId === owner.id)
        );
        const isIndividualParent = parentOwners?.some(
          (owner) => owner.type === OwnerType.INDIVIDUAL
        );
        return (
          <IndividualOwnerForm
            index={index}
            isFirstLevel={isFirstLevel}
            isIndividualParent={isIndividualParent}
            handlePercentageChange={handlePercentageChange}
          />
        );
      }
      case OwnerType.LEGAL_ENTITY:
        return (
          <LegalResidentOwnerForm
            index={index}
            isFirstLevel={isFirstLevel}
            handlePercentageChange={handlePercentageChange}
          />
        );
      case OwnerType.FREE_FLOAT:
        return <FreeFloatOwnerForm index={index} handlePercentageChange={handlePercentageChange} />;
      case OwnerType.SHAREHOLDER_GROUP:
        return (
          <SharedGroupOwnerForm index={index} handlePercentageChange={handlePercentageChange} />
        );
      case OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP:
        return (
          <IndividualOwnerForm
            index={index}
            handlePercentageChange={handlePercentageChange}
            isWithoutFormalOwnership
          />
        );
      default:
        break;
    }
  };

  return (
    <Modal open={index >= 0} onClose={handleClose}>
      <Paper sx={styles.modalContainer}>
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Інформація про власника
        </Typography>
        {getOwnerForm(index)}
        <Button variant="contained" onClick={handleClose}>
          Продовжити
        </Button>
      </Paper>
    </Modal>
  );
};
